import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LoginComponent } from './modules/_shared/routes/user/login/login.component';
import { ReloadPageComponent } from './modules/_shared/routes/reload-page/reload-page.component';
import { DashboardComponent } from './modules/_shared/routes/dashboard/dashboard.component';
import { AppRoutes, DebugModule, FormRouteComponent, NotAuthorizedComponent, PageNotFoundComponent } from '@hutsix/ngxh6';
import { CspAuthGuardService } from './modules/_shared/services/csp-auth-guard.service';
import { AuthGuardService } from './modules/_shared/services/auth-guard/auth-guard.service';

// After a deploy the previously-hashed lazy chunks are removed from the
// origin, so a client still running the old index.html requests a chunk that
// no longer exists. CloudFront then serves index.html (text/html) in place of
// the missing .js and the browser rejects the module script. The failure
// surfaces differently per builder/browser:
//   - ChunkLoadError                                       (webpack builds)
//   - "'text/html' is not a valid JavaScript MIME type."   (WebKit/Safari)
//   - "Failed to fetch dynamically imported module: …"     (Chrome)
//   - "error loading dynamically imported module"          (Firefox)
//   - "Importing a module script failed."                  (Safari)
// The @angular/build:application (esbuild) builder emits native ESM lazy
// chunks, so a stale-chunk load throws a plain TypeError with one of the
// messages above rather than a named ChunkLoadError.
export function isChunkLoadError(e: any): boolean {
    if (e?.name === 'ChunkLoadError') return true;
    const message = typeof e?.message === 'string' ? e.message : '';
    return (
        /is not a valid JavaScript MIME type/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
    );
}

// Guard against a reload loop: if a single reload doesn't resolve the failure
// (a genuinely broken deploy rather than a merely stale client) we report the
// error instead of reloading forever.
const CHUNK_RELOAD_KEY = 'chunkLoadReloadAt';
const CHUNK_RELOAD_WINDOW_MS = 10000;

function shouldReloadForChunkError(): boolean {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
        if (Number.isFinite(last) && last > 0 && Date.now() - last < CHUNK_RELOAD_WINDOW_MS) {
            return false;
        }
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
        // sessionStorage can be unavailable (private mode / disabled storage);
        // reload without the loop guard rather than swallow the recovery.
    }
    return true;
}

function handleChunkLoadError(e: any): never {
    if (isChunkLoadError(e) && shouldReloadForChunkError()) {
        window.location.reload();
    }
    throw e;
}

const routes: AppRoutes = [
    { path: 'reload', component: ReloadPageComponent },
    { path: 'login', component: LoginComponent },
    { path: 'form/:slug', canActivate: [AuthGuardService], component: FormRouteComponent },
    { path: 'form/:slug/:id', canActivate: [AuthGuardService], component: FormRouteComponent },
    {
        path: '',
        canActivate: [AuthGuardService],
        component: DashboardComponent,
    },
    {
        path: 'csp',
        canActivate: [CspAuthGuardService],
        data: { roles: ['ROLE_CSP_USER'] },
        loadChildren: () => import('./modules/csp/csp.module').then(m => m.CspModule).catch(handleChunkLoadError),
    },
    {
        path: 'user-admin',
        loadChildren: () => import('./modules/user-admin/user-admin.module').then(m => m.UserAdminModule).catch(handleChunkLoadError),
    },
    {
        path: 'debug',
        canActivate: [CspAuthGuardService],
        data: { roles: ['ROLE_CSP_ADMIN'] },
        loadChildren: () => DebugModule,
    },
    { path: 'unauthorised', canActivate: [CspAuthGuardService], component: NotAuthorizedComponent },
    { path: '**', canActivate: [CspAuthGuardService], component: PageNotFoundComponent },
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule],
})
export class AppRoutingModule {}
